import { useState } from 'react';
import styled from 'styled-components';
import type { LogEntryView } from './logic';

const Item = styled.article`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  font-size: 13px;
  line-height: 1.5;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Action = styled.span`
  font-weight: 700;
`;

const Meta = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  white-space: nowrap;
`;

const Line = styled.div`
  overflow-wrap: anywhere;
`;

const Label = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Arrow = styled.span`
  margin: 0 6px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MoreButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  cursor: pointer;
`;

/** Пар «было → стало» перенумерации, видимых сразу. */
const PAIRS_SHOWN = 12;

/** Запись журнала правок: кто внёс (ФИО, роль), когда, действие, комплект и
 *  строка, «было → стало», лист, причина, комментарий, документ изменения. */
export function LogEntry({ view: v }: { view: LogEntryView }) {
  const [allPairs, setAllPairs] = useState(false);
  const pairs = allPairs ? v.pairs : v.pairs.slice(0, PAIRS_SHOWN);
  return (
    <Item>
      <Head>
        <span>
          <Action>{v.action}</Action>
          {v.place && <> · {v.place}</>}
        </span>
        <Meta>
          № {v.id} · {v.when}
        </Meta>
      </Head>
      <Line>
        <Label>Кто внёс:</Label> {v.who}
        {v.roles && ` (${v.roles})`}
      </Line>
      {(v.before || v.after) && (
        <Line>
          <Label>Было:</Label> {v.before || '—'}
          <Arrow>→</Arrow>
          <Label>стало:</Label> {v.after || '—'}
        </Line>
      )}
      {v.pairs.length > 0 && (
        <Line>
          <Label>Номера «было → стало»:</Label>{' '}
          {pairs.map(([from, to]) => `${from} → ${to ?? 'без номера'}`).join('; ')}
          {v.pairs.length > PAIRS_SHOWN && (
            <>
              {' '}
              <MoreButton type="button" onClick={() => setAllPairs((x) => !x)}>
                {allPairs ? 'свернуть' : `ещё ${v.pairs.length - PAIRS_SHOWN}`}
              </MoreButton>
            </>
          )}
        </Line>
      )}
      {v.sheet && (
        <Line>
          <Label>Лист РД / место в документе:</Label> {v.sheet}
        </Line>
      )}
      {v.reason && (
        <Line>
          <Label>Причина:</Label> {v.reason}
        </Line>
      )}
      {v.comment && (
        <Line>
          <Label>Комментарий:</Label> {v.comment}
        </Line>
      )}
      {v.doc && (
        <Line>
          <Label>Документ изменения:</Label> {v.doc}
        </Line>
      )}
    </Item>
  );
}
